import { ThemeEvents } from '@theme/events';

/**
 * Ruts — Completa el look
 *
 * Orquesta el bloque de productos complementarios dentro del carrito.
 * - Recorre las líneas del carrito (último -> primero) como semillas.
 * - Pide a cada semilla sus complementarios oficiales (Search & Discovery)
 *   vía el endpoint product_recommendations + Section Rendering API.
 * - Agrega, deduplica, excluye agotados y los ya presentes en el carrito,
 *   y corta al máximo configurado (1 o 2).
 * - Reutiliza el Quick Add existente (las tarjetas vienen con <quick-add-component>).
 * - Si no hay complementarios, no muestra nada ni reserva espacio.
 *
 * Ruts fix (2026-07-25) — además del listener de 'cart:update', expone
 * refresh(): un método público e idempotente invocado directamente por el
 * ciclo central de actualización del carrito (component-cart-items.js).
 */
class CartRecommendations extends HTMLElement {
  #controller = null;
  #fetchController = null;
  #cache = new Map();
  #runToken = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal: this.#controller.signal,
    });
    this.update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#fetchController?.abort();
  }

  #onCartUpdate = () => {
    this.#cache.clear();
    this.update();
  };

  get #root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  #hide() {
    this.replaceChildren();
    this.hidden = true;
  }

  async #fetchSeed(productId) {
    const base = this.dataset.url;
    const sectionId = this.dataset.sectionId;
    if (!base || !sectionId) return '';

    const url = `${base}?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(productId)}&intent=complementary&limit=10`;

    const cached = this.#cache.get(url);
    if (cached != null) return cached;

    try {
      const res = await fetch(url, { signal: this.#fetchController?.signal });
      if (!res.ok) return '';
      const text = await res.text();
      this.#cache.set(url, text);
      return text;
    } catch (e) {
      return '';
    }
  }

  refresh() {
    this.#cache.clear();
    this.update();
  }

  async update() {
    const token = ++this.#runToken;
    const max = Math.max(1, parseInt(this.dataset.max || '2', 10) || 2);

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    let cart;
    try {
      const res = await fetch(`${this.#root}cart.js`, {
        headers: { Accept: 'application/json' },
        signal: this.#fetchController.signal,
      });
      if (!res.ok) return;
      cart = await res.json();
    } catch (e) {
      return;
    }
    if (token !== this.#runToken) return;

    const items = (cart && cart.items) || [];
    if (!items.length) {
      this.#hide();
      return;
    }

    const inCart = new Set(items.map((i) => String(i.product_id)));

    const seeds = [];
    const seenSeed = new Set();
    for (let i = items.length - 1; i >= 0; i--) {
      const pid = String(items[i].product_id);
      if (seenSeed.has(pid)) continue;
      seenSeed.add(pid);
      seeds.push(pid);
    }

    const parser = new DOMParser();
    const chosen = [];
    const chosenIds = new Set();

    for (const seed of seeds) {
      if (chosen.length >= max) break;
      const html = await this.#fetchSeed(seed);
      if (token !== this.#runToken) return;
      if (!html.trim()) continue;

      const doc = parser.parseFromString(html, 'text/html');
      const cards = doc.querySelectorAll('product-card.cart-rec__card');
      for (const card of cards) {
        const pid = card.getAttribute('data-product-id') || '';
        if (!pid || inCart.has(pid) || chosenIds.has(pid)) continue;
        chosenIds.add(pid);
        chosen.push(card);
        if (chosen.length >= max) break;
      }
    }

    if (token !== this.#runToken) return;

    if (!chosen.length) {
      this.#hide();
      return;
    }

    const frag = document.createDocumentFragment();

    const heading = document.createElement('p');
    heading.className = 'cart-rec__heading';
    heading.textContent = this.dataset.title || 'COMPLETA EL LOOK';
    frag.appendChild(heading);

    const list = document.createElement('div');
    list.className = 'cart-rec__list';
    for (const card of chosen) {
      list.appendChild(document.importNode(card, true));
    }
    frag.appendChild(list);

    this.replaceChildren(frag);
    this.hidden = false;
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
